/**
 * CAP OData V4 client — talks to the ppm-cap service (`/ppm`) that hosts the
 * app-owned PPM entities in SAP HANA Cloud and federates S/4 (plan §9B).
 *
 * This is the SECOND backend the `sap` mode can reach, alongside the direct
 * `/api/s4/*` services (odataClient.ts). App-owned entities (portfolios, risks,
 * baselines, forecasts, budget, …) resolve here; live S/4 truth (actuals, WBS)
 * stays on the direct services or is federated by CAP server-side.
 *
 * Base URL is `VITE_CAP_BASE_URL` (default `/ppm`): in dev the React app proxies
 * `/ppm` to the local `cds watch` server; in prod it points at the deployed CAP
 * app on BTP. CAP returns camelCase element names matching the app types, so
 * mapping is near-identity — only the `ID` key and `<assoc>_ID` foreign keys
 * are normalized to the app's `id` / `<assoc>Id` convention.
 */

import { SapBackendError } from './errors'

const CAP_BASE = ((): string => {
  try {
    return (import.meta.env.VITE_CAP_BASE_URL as string | undefined) ?? '/ppm'
  } catch {
    return '/ppm'
  }
})()

interface ODataCollection<T> {
  '@odata.context'?: string
  value: T[]
}

interface ODataErrorBody {
  error?: { code?: string; message?: string | { value?: string } } | string
  message?: string
}

async function unpackError(res: Response): Promise<SapBackendError> {
  let code: string | null = null
  let message = `CAP returned HTTP ${res.status}`
  try {
    const body = (await res.json()) as ODataErrorBody
    if (body && typeof body.error === 'object' && body.error) {
      const m = body.error.message
      message = (typeof m === 'string' ? m : m?.value) || message
      code = body.error.code ?? null
    } else if (typeof body?.message === 'string') {
      message = body.message
    }
  } catch {
    /* keep default */
  }
  return new SapBackendError(res.status, code, message)
}

/** OData string literal: wrap in single quotes, double internal quotes. */
export function capLiteral(v: string): string {
  return `'${v.replace(/'/g, "''")}'`
}

/** Normalize a CAP row to the app's shape: `ID` → `id`, `<assoc>_ID` → `<assoc>Id`.
 *  Everything else (already camelCase) passes through untouched. */
function mapCapRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row)) {
    if (k === 'ID') out.id = v
    else if (k.endsWith('_ID')) out[`${k.slice(0, -3)}Id`] = v
    else out[k] = v
  }
  return out
}

export interface CapListOpts {
  filter?: string
  expand?: string
  orderby?: string
  top?: number
}

/** GET an entity-set collection, mapped to the app's row shape. */
export async function capList<T>(entitySet: string, opts?: CapListOpts): Promise<T[]> {
  const qs = new URLSearchParams()
  if (opts?.filter) qs.set('$filter', opts.filter)
  if (opts?.expand) qs.set('$expand', opts.expand)
  if (opts?.orderby) qs.set('$orderby', opts.orderby)
  qs.set('$top', String(opts?.top ?? 5000))
  const url = `${CAP_BASE}/${entitySet}?${qs.toString()}`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw await unpackError(res)
  const body = (await res.json()) as ODataCollection<Record<string, unknown>>
  return (body.value ?? []).map(mapCapRow) as T[]
}

/** Convenience: filter an entity set by a foreign-key id (e.g. project_ID). */
export function capListByFk<T>(entitySet: string, fkField: string, id?: string): Promise<T[]> {
  return capList<T>(entitySet, id ? { filter: `${fkField} eq ${capLiteral(id)}` } : undefined)
}

/** Map an app row back to CAP shape for writes (`id` → `ID`, `<x>Id` → `<x>_ID`). */
function toCapRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row)) {
    if (k === 'id') out.ID = v
    else if (/Id$/.test(k) && k !== 'id') out[`${k.slice(0, -2)}_ID`] = v
    else out[k] = v
  }
  return out
}

export async function capCreate<T>(entitySet: string, data: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${CAP_BASE}/${entitySet}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(toCapRow(data)),
  })
  if (!res.ok) throw await unpackError(res)
  return mapCapRow(await res.json()) as T
}

export async function capUpdate<T>(entitySet: string, id: string, patch: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${CAP_BASE}/${entitySet}(${capLiteral(id)})`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(toCapRow(patch)),
  })
  if (!res.ok) throw await unpackError(res)
  return mapCapRow(await res.json()) as T
}

export async function capDelete(entitySet: string, id: string): Promise<void> {
  const res = await fetch(`${CAP_BASE}/${entitySet}(${capLiteral(id)})`, { method: 'DELETE' })
  if (!res.ok && res.status !== 404) throw await unpackError(res)
}
